import { useCallback, useContext, useRef } from "react";
import { audioContext, hasGestured } from "../contexts/AudioContext";
import { LOGS } from "../utils/logging";
import { useNavigate } from "react-router";
import type { VerbName } from "../../config/interactive/types";
import { ALL_STATIONS } from "../../config/audio/radioConfig";
import { VerbPlaySoundData, RadioName, VerbSetVolumeData } from "../../config/audio/types";
import { radioVolumeGetNextStep } from "../utils/utils";
import { useGlobalEntityState } from "../contexts/GlobalStateContext";
import { VerbChangeStateData } from "../../config/types";
import {
	eventRadioStart,
	eventRadioStationChanged,
	eventRadioStop,
	eventRadioVolumeChanged,
} from "../utils/gtag";

export default function useVerbHandlers() {
	const { radioState, setRadioState, playSound } = useContext(audioContext);
	const { hasUserGestured } = useContext(audioContext);
	const { updateGlobalEntityState } = useGlobalEntityState();
	const navigate = useNavigate();
	const startAt = useRef(0);

	/**
	 * Handler for NAVIGATE action, used for navigating to react router routes
	 * and redirecting to external modules, such as chess
	 */
	const navigateTo = (data: { url: string; delay?: number }) => {
		const nav = (url: string) => {
			const isRedirect = url.startsWith("http");
			if (isRedirect) {
				window.location.href = url;
				return;
			}
			incrementSessionNavCount();
			navigate(url);
		};

		if (data.delay) {
			setTimeout(() => {
				nav(data.url);
			}, data.delay);
			return;
		}

		nav(data.url);
	};

	/**
	 * Handler for TOGGLE_RADIO action
	 */
	const toggleRadio = useCallback(
		(data: RadioName) => {
			LOGS.AUDIO && console.log("Radio state going to be changed: ", radioState);
			const radio = radioState[data];
			if (!radio) {
				LOGS.AUDIO && console.warn("no radio matching name,", data);
			}

			if (!radio.isOn) {
				startAt.current = Date.now();
				eventRadioStart(radio.station, radio.gain);
			} else {
				eventRadioStop(radio.station, radio.gain, Date.now() - startAt.current);
			}

			setRadioState((prev) => ({
				...prev,
				[data]: {
					...prev[data],
					isOn: !prev[data].isOn,
				},
			}));
		},
		[hasGestured, radioState],
	);

	/**
	 * Handler for STATION_CHANGE action
	 */
	const radioChangeStation = useCallback(
		(data: RadioName) => {
			if (!hasUserGestured) return;
			LOGS.AUDIO && console.log("Changing the station for radio: ", data);
			const radio = radioState[data];
			if (!radio) {
				LOGS.AUDIO && console.warn("no radio matching name,", data);
			}
			const currentStation = radioState[data].station;
			const indexOfCurrentStation = ALL_STATIONS.indexOf(currentStation);
			const nextStation = ALL_STATIONS[(indexOfCurrentStation + 1) % ALL_STATIONS.length];
			eventRadioStationChanged(currentStation, nextStation);
			setRadioState((prev) => ({
				...prev,
				[data]: {
					...prev[data],
					station: nextStation,
				},
			}));
		},
		[hasUserGestured, radioState],
	);

	/**
	 * Handler for PLAY_SOUND action
	 */
	const playOneShotSound = useCallback(
		(data: VerbPlaySoundData) => {
			const { playlist, gain } = data;
			playSound(playlist, gain);
		},
		[hasUserGestured],
	);

	/**
	 * Handler for BACK action
	 * Tracks internal navigation to prevent going outside the app
	 */
	const back = () => {
		const inAppNavigationCount = getSessionNavCount();
		if (inAppNavigationCount > 0) {
			decrementSessionNavCount();
			navigate(-1);
		}
	};

	/**
	 * Handler for SET_VOLUME action
	 */
	const setVolume = useCallback(
		(data: VerbSetVolumeData) => {
			if (!hasUserGestured) return;
			LOGS.AUDIO && console.log("Setting volume for radio:", data);
			const radio = radioState[data.radio];
			if (!radio) {
				LOGS.AUDIO && console.warn("no radio matching name,", data.radio);
				return;
			}

			const [newLevel, newGain] = radioVolumeGetNextStep(
				radio.volumeLevel,
				data.direction,
				radio.config,
			);
			eventRadioVolumeChanged(radio.volumeLevel, newLevel, radio.station);
			LOGS.AUDIO &&
				console.log(
					`Volume changed from level ${radio.volumeLevel} (${radio.gain}) to level ${newLevel} (${newGain})`,
				);

			setRadioState((prev) => ({
				...prev,
				[data.radio]: {
					...prev[data.radio],
					volumeLevel: newLevel,
				},
			}));
		},
		[hasUserGestured, radioState],
	);

	/**
	 * Handler for CHANGE_SCENE_STATE action
	 */
	const changeGlobalEntityState = useCallback(
		(data: VerbChangeStateData) => {
			updateGlobalEntityState(data);
		},
		[updateGlobalEntityState],
	);

	return {
		NAVIGATE: navigateTo,
		BACK: back,
		TOGGLE_RADIO: toggleRadio,
		CHANGE_STATION: radioChangeStation,
		PLAY_SOUND: playOneShotSound,
		SET_VOLUME: setVolume,
		CHANGE_SCENE_STATE: changeGlobalEntityState,
	} satisfies Record<VerbName, (...args: any[]) => void>;
}

const getAppNavigation = () => sessionStorage.getItem("app_navigation_count");

function decrementSessionNavCount() {
	const currentCount = parseInt(getAppNavigation() || "0", 10);
	sessionStorage.setItem("app_navigation_count", (currentCount - 1).toString());
}

function incrementSessionNavCount() {
	const currentCount = parseInt(getAppNavigation() || "0", 10);
	sessionStorage.setItem("app_navigation_count", (currentCount + 1).toString());
}

function getSessionNavCount() {
	const hasInternalHistory = getAppNavigation();
	return hasInternalHistory ? parseInt(hasInternalHistory, 10) : 0;
}
